import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, GithubLogoIcon, TerminalWindowIcon, WarningIcon } from '@phosphor-icons/react';

import { Alert } from '@/ui/Alert';
import { Card } from '@/ui/Card';
import { SpinnerBlock } from '@/ui/Spinner';
import { Button } from '@/ui/button/Button';
import { Input } from '@/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/Select';
import { useOptionalMe } from '@/api-models/user';
import { useTeams } from '@/api-models/teams';
import { decideCliDeviceRequest, useCliDeviceRequest } from '@/api-models/cli-device';
import { AuthLayout, OAuthButton } from '@/features/auth/components';

type Outcome = 'approved' | 'denied';

const CLI_FEATURES = [
  {
    title: 'Code from your own terminal',
    description: 'The same agent, running where you already work',
    icon: 'terminal' as const,
  },
  {
    title: 'Scoped to the model gateway',
    description: "A CLI token can't act on your account",
    icon: 'shield' as const,
  },
  {
    title: 'Revoke any time',
    description: 'Settings → Security lists every active token',
    icon: 'key' as const,
  },
];

/**
 * Approval page for the `coding-agent` CLI's `--login`.
 *
 * Deliberately **not** wrapped in `AuthProvider`. That component redirects an unauthenticated
 * visitor to `/login` with no way back, which would silently drop the code from the URL — and
 * this page is the one place people arrive cold, from a terminal, often on a machine where they
 * are not signed in. Instead it checks the session itself and offers a sign-in link that carries
 * the return path, using the OAuth `?redirect=` hook (filtered server-side to same-origin paths).
 *
 * The security model is worth stating plainly, because the UI copy is the control: an attacker
 * can start a device flow and send someone else their own approval link. Entropy and rate limits
 * do nothing about that. What defeats it is the human comparing the code shown here against the
 * one printed in their own terminal — which the attacker never saw. So the code is the most
 * prominent thing on the page, approval is always an explicit click, and nothing is approved as
 * a side effect of the page loading.
 */
export const CliLoginPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const codeFromUrl = searchParams.get('code');

  const [typedCode, setTypedCode] = useState('');
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [isDeciding, setIsDeciding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Session check that does not redirect, unlike `AuthProvider`.
  const { me, isLoading: isLoadingMe } = useOptionalMe();
  const { teams } = useTeams();

  // Bill the personal team until the user picks another one.
  const [pickedTeamId, setTeamId] = useState<string | undefined>(undefined);
  const teamId = pickedTeamId ?? me?.personalTeamId;

  // Take the code out of the address bar once it has been read. It stays in component state, so
  // nothing breaks — this only keeps it out of browser history and out of shot during a screen
  // share.
  useEffect(() => {
    if (codeFromUrl) {
      setTypedCode(codeFromUrl);
      const next = new URLSearchParams(searchParams);
      next.delete('code');
      setSearchParams(next, { replace: true });
    }
  }, [codeFromUrl, searchParams, setSearchParams]);

  const activeCode = typedCode.trim();
  const {
    request,
    error: lookupError,
    isLoading,
  } = useCliDeviceRequest(me && activeCode.length >= 8 ? activeCode : null);

  // Countdown against the 5-minute device-code TTL. This is the *browser's* clock measured
  // against a server-issued timestamp, so a badly skewed machine could read "expired" on a live
  // code — which is why running out only disables Approve and adds a line, rather than replacing
  // the view. The authoritative answer is still the server's 404 on decide, handled below. It
  // also deliberately drives nothing but this label: `useCliDeviceRequest` keeps
  // `revalidateOnFocus: false` to avoid a bogus expired flash, and a refetch here would undo that.
  const expiresAtMs = request ? Date.parse(request.expiresAt) : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (expiresAtMs === null) return;
    // Reseed on the way in: `now` is otherwise as old as the mount, so a code typed after a long
    // pause on the entry screen would flash a countdown minutes too generous until the first tick.
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAtMs]);
  const secondsLeft = expiresAtMs === null ? null : Math.max(0, Math.round((expiresAtMs - now) / 1000));
  const hasExpired = secondsLeft === 0;

  const handleDecide = async (approved: boolean) => {
    if (!request) return;
    setIsDeciding(true);
    setError(null);
    try {
      await decideCliDeviceRequest(request.userCode, approved, approved ? teamId : undefined);
      setOutcome(approved ? 'approved' : 'denied');
    } catch {
      setError('That code is no longer valid. Start a new sign-in from your terminal.');
    } finally {
      setIsDeciding(false);
    }
  };

  if (isLoadingMe) {
    return <SpinnerBlock message="Loading..." className="h-screen" />;
  }

  if (!me) {
    // Carry the code through sign-in so the round trip doesn't lose it.
    const returnTo = activeCode ? `/cli-login?code=${encodeURIComponent(activeCode)}` : '/cli-login';
    return (
      <Shell>
        <div className="flex flex-col items-center text-center">
          <h2 className="text-display font-bold tracking-tight text-fg mb-3">Sign in to continue</h2>
          <p className="mt-2 text-md text-fg-secondary">
            You&rsquo;re approving a sign-in for the Zooid CLI. Sign in first, and we&rsquo;ll bring you back here.
          </p>

          <div className="mt-10 w-full max-w-80">
            <OAuthButton
              icon={<GithubLogoIcon size={20} weight="fill" />}
              label="Continue with GitHub"
              onClick={() => {
                window.location.href = `/api/v1/auth/github?redirect=${encodeURIComponent(returnTo)}`;
              }}
            />
          </div>
        </div>
      </Shell>
    );
  }

  if (outcome === 'approved') {
    return (
      <Shell>
        <OutcomeCard
          icon={<CheckCircleIcon size={48} weight="fill" className="text-success mb-4" />}
          title="You’re signed in"
        >
          Your terminal should continue on its own. You can close this tab.
        </OutcomeCard>
      </Shell>
    );
  }

  if (outcome === 'denied') {
    return (
      <Shell>
        <OutcomeCard
          icon={<WarningIcon size={48} weight="fill" className="text-fg-secondary mb-4" />}
          title="Request denied"
        >
          No token was created. If this wasn&rsquo;t you, nothing further is needed.
        </OutcomeCard>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex w-full flex-col items-center">
        <Card radius="2xl" padding="none" className="w-full max-w-110 shadow-sm p-6 sm:p-8">
          <h2 className="text-[26px] font-bold tracking-tight text-fg mb-1.5">Approve a device</h2>
          <p className="text-sm text-fg-secondary mb-6">Signed in as {me.email}</p>

          <div className="flex flex-col gap-5">
            {!request && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="cli-code" className="text-xs font-medium text-fg-secondary">
                  Enter the code from your terminal
                </label>
                <Input
                  id="cli-code"
                  autoFocus
                  placeholder="XXXX-XXXX"
                  value={typedCode}
                  onChange={(e) => setTypedCode(e.target.value.toUpperCase())}
                />
                {isLoading && <p className="text-xs text-fg-secondary">Checking…</p>}
                {lookupError && activeCode.length >= 8 && (
                  <Alert variant="danger" className="mt-1">
                    That code isn&rsquo;t valid or has expired.
                  </Alert>
                )}
              </div>
            )}

            {request && (
              <>
                {/* The comparison below is the actual security control — see the file header. */}
                <div className="flex flex-col items-center gap-2 rounded-xl border border-line bg-sunken py-7">
                  <span className="text-2xs font-medium uppercase tracking-widest text-fg-muted">Code</span>
                  <span className="font-mono text-[34px] font-bold tracking-[0.22em] text-fg">{request.userCode}</span>
                  {secondsLeft !== null && !hasExpired && (
                    <span className={secondsLeft < 60 ? 'text-xs text-danger' : 'text-xs text-fg-secondary'}>
                      Expires in {formatCountdown(secondsLeft)}
                    </span>
                  )}
                </div>

                <Alert variant="warning" title="Check the code" icon={<WarningIcon size={18} weight="fill" />}>
                  Only approve if this code matches the one printed in your terminal. If you didn&rsquo;t start this,
                  deny it — someone may be trying to sign in as you.
                </Alert>

                <dl className="flex flex-col gap-2 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-fg-secondary">Device</dt>
                    {/* Self-reported by the requesting machine: rendered as plain text, never a link. */}
                    <dd className="text-right text-fg">
                      <TerminalWindowIcon size={14} className="mr-1 inline" />
                      {request.deviceLabel ?? 'Unknown'}{' '}
                      <span className="text-xs text-fg-secondary">(reported by the device)</span>
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-fg-secondary">From IP</dt>
                    <dd className="text-right font-mono text-fg">{request.requestIp ?? 'Unknown'}</dd>
                  </div>
                </dl>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="cli-team" className="text-xs font-medium text-fg-secondary">
                    Workspace to bill
                  </label>
                  <Select value={teamId} onValueChange={setTeamId}>
                    <SelectTrigger id="cli-team">
                      <SelectValue placeholder="Select a workspace" />
                    </SelectTrigger>
                    <SelectContent>
                      {teams.map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {hasExpired && (
                  <Alert variant="danger">This code has expired. Start a new sign-in from your terminal.</Alert>
                )}

                {error && <Alert variant="danger">{error}</Alert>}

                <div className="flex gap-3 *:flex-1">
                  <Button
                    variant="primary"
                    size={9}
                    fullWidth
                    isLoading={isDeciding}
                    disabled={isDeciding || !teamId || hasExpired}
                    onClick={() => handleDecide(true)}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="secondary"
                    size={9}
                    fullWidth
                    disabled={isDeciding}
                    onClick={() => handleDecide(false)}
                  >
                    Deny
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </Shell>
  );
};

const formatCountdown = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const Shell = ({ children }: { children: React.ReactNode }) => (
  <AuthLayout
    headline={
      <>
        Your terminal,
        <br />
        signed in.
      </>
    }
    subline="Approve this request to let the Zooid CLI reach your workspace. The code it shows must match the one your terminal printed."
    features={CLI_FEATURES}
    pageTitle="Approve a device — Zooid"
  >
    {children}
  </AuthLayout>
);

const OutcomeCard = ({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex w-full flex-col items-center">
    <Card radius="2xl" padding="none" className="w-full max-w-110 flex flex-col items-center text-center shadow-sm p-8">
      {icon}
      <h2 className="text-display-sm font-bold tracking-tight text-fg mb-3">{title}</h2>
      <p className="text-md text-fg-secondary leading-relaxed">{children}</p>
    </Card>
  </div>
);
